import React from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Link
} from "@mui/material";
import {
  LocalHospital,
  Handshake,
  CalendarMonth,
  RocketLaunch,
  Fingerprint,
  Code,
  Send,
  Email
} from "@mui/icons-material";

// Asset e Componenti
import background from "../../src/wp.jpg";
import ExcelImg from "../assets/img/excel.png";
import Navbar from "components/Navbars/AuthNavbar.js";
import Footer from "components/Footers/FooterLanding.js";

function Landing() {
  const { t } = useTranslation();

  return (
    <>
      <Navbar transparent />
      <main>
        {/* HERO SECTION */}
        <Box
          sx={{
            position: "relative",
            pt: { xs: 12, md: 4 },
            pb: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: { xs: "80vh", md: "70vh" },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `url(${background})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              "&::before": {
                content: '""',
                position: "absolute",
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0,0,0,0.75)", // Black overlay opacity 75%
              },
            }}
          />

          <Container sx={{ position: "relative", zIndex: 1 }}>
            <Grid container justifyContent="center">
              <Grid item xs={12} lg={8} sx={{ textAlign: "center", color: "white" }}>
                <Typography variant="h2" component="h1" sx={{ 
                    fontWeight: 700, 
                    mb: 2, 
                    color: 'white',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.6)' // Ombra nera semi-trasparente
                  }}>
                  {t("welcome")}
                </Typography>
                <Typography variant="h5" sx={{ color: "rgba(255, 255, 255, 0.85)", lineHeight: 1.6 }}>
                  {t("subWelcome")}
                </Typography>
              </Grid>
            </Grid>
          </Container>

          {/* SVG Separator (Diagonal) */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              width: "100%",
              overflow: "hidden",
              lineHeight: 0,
            }}
          >
            <svg
              viewBox="0 0 2560 100"
              preserveAspectRatio="none"
              style={{ height: "70px", width: "100%" }}
            >
              <polygon points="2560 0 2560 100 0 100" style={{ fill: "#e2e8f0" }} />
            </svg>
          </Box>
        </Box>

        {/* FEATURES CARDS SECTION */}
        <Box sx={{ bgcolor: "#e2e8f0", pb: 10, mt: -10, position: "relative", zIndex: 2 }}>
          <Container>
            <Grid container spacing={4}>
              {[
                { title: t("Shifts"), sub: t("subShifts"), icon: <LocalHospital fontSize="large" />, color: "#f87171" },
                { title: t("volunteers"), sub: t("subVolunteers"), icon: <Handshake fontSize="large" />, color: "#60a5fa" },
                { title: t("Calendars"), sub: t("subCalendars"), icon: <CalendarMonth fontSize="large" />, color: "#4ade80" }
              ].map((item, index) => (
                <Grid item xs={12} md={4} key={index} sx={{ pt: index === 1 ? { md: 0 } : { md: 6, xs: 0 } }}>
                  <Paper
                    elevation={4}
                    sx={{
                      p: 4,
                      textAlign: "center",
                      borderRadius: 4,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: 6
                      }
                    }}
                    
                  >
                    <Avatar sx={{ bgcolor: item.color, mb: 3, width: 60, height: 60, boxShadow: 3 }}>
                      {item.icon}
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.sub}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* DESCRIPTION SECTION WITH LINK */}
            <Grid container spacing={8} alignItems="center" sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Avatar sx={{ bgcolor: "white", color: "grey.600", width: 64, height: 64, mb: 3, boxShadow: 2 }}>
                  <Handshake />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: "text.primary" }}>
                  {t("pleasureManage")}
                </Typography>
                <Typography variant="body1" sx={{ color: "text.secondary", mb: 2, fontSize: "1.1rem" }}>
                  {t("pleasureFirst")}
                </Typography>
                <Typography variant="body1" sx={{ color: "text.secondary", mb: 4, fontSize: "1.1rem" }}>
                  {t("pleasureSecond")}
                </Typography>
                <Link
                  component={RouterLink}
                  to="/"
                  sx={{ fontWeight: 700, color: "text.primary", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                >
                  {t("checkVoluntHUB")}
                </Link>
              </Grid>

              <Grid item xs={12} md={5} sx={{ ml: "auto" }}>
                <Paper sx={{ bgcolor: "primary.main", color: "white", borderRadius: 4, overflow: "hidden", position: "relative" }}>
                  <Box component="img" src="assets/img/calendar.png" sx={{ width: "100%" }} />
                  <Box sx={{ p: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {t("onlineService")}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 , color: "white"}}>
                      {t("subOnlineService")}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* EXCEL SECTION */}
        <Box sx={{ py: 10, bgcolor: "white" }}>
          <Container>
            <Grid container spacing={8} alignItems="center">
              <Grid item xs={12} md={5}>
                <Box
                  component="img"
                  src={ExcelImg}
                  sx={{ width: "100%", borderRadius: 4, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ ml: "auto" }}>
                <Avatar sx={{ bgcolor: "blue.100", color: "primary.main", width: 64, height: 64, mb: 3 }}>
                  <RocketLaunch />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
                  {t("exportExcel")}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  {t("exportExcelParagraph")}
                </Typography>
                <List>
                  {[
                    { icon: <Fingerprint />, text: t("excelCompatibility") },
                    { icon: <Code />, text: t("easyClick") },
                    { icon: <Send />, text: t("easyPrint") }
                  ].map((item, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: 500, color: "text.secondary" }} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* CONTACT SECTION */}
        <Box component="section" sx={{ py: 10, bgcolor: "#1a202c", color: "white", position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              width: "100%",
              height: "80px",
              mt: "-80px",
              overflow: "hidden",
            }}
          >
            <svg viewBox="0 0 2560 100" preserveAspectRatio="none" style={{ height: "100%", width: "100%" }}>
              <polygon points="2560 0 2560 100 0 100" style={{ fill: "#1a202c" }} />
            </svg>
          </Box>
          <Container>
            <Grid container justifyContent="center">
              <Grid item xs={12} lg={6} sx={{ textAlign: "center" }}>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }}>
                  {t("contactUs")}
                </Typography>
                <Typography variant="h6" sx={{ color: "grey.500", mb: 6, fontWeight: 400 }}>
                  {t("contactUsParagraph")}
                </Typography>
                <IconButton
                  href="mailto:alex.corti@lombardia.cri.it"
                  sx={{
                    color: "white",
                    bgcolor: "rgba(255,255,255,0.1)",
                    p: 3,
                    "&:hover": { bgcolor: "primary.main", transform: "scale(1.1)" },
                    transition: "all 0.3s"
                  }}
                >
                  <Email sx={{ fontSize: 40 }} />
                </IconButton>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </main>
      <Footer />
    </>
  );
}

export default Landing;